/**
 * PromChallengesPage — /prom/:slug/challenges
 *
 * Single-page hub of all 50 prom challenges. NO tabs.
 * Three sections rendered top-to-bottom:
 *   🔥 Trending — top 5 by entries
 *   🧑‍🏫 Teacher moments — category=teacher
 *   🏆 All challenges — full list
 *
 * Each card shows title, vote count, 3 preview thumbnails, and a CTA
 * that opens the per-challenge leaderboard (which is also where
 * submission happens).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Trophy, Flame, ChalkboardTeacher, ArrowRight, Sparkle, GraduationCap } from '@phosphor-icons/react';
import { trackGrowth, getAnonId } from '../lib/growthTrack';
import LiveActivityTicker from '../components/LiveActivityTicker';
import { getMediaUrl } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  spotlight: 'Spotlight',
  fun: 'Fun',
  teacher: 'Teacher',
  social: 'Social',
  memory: 'Memory',
};

export default function PromChallengesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(ev.data);
        const list = await axios.get(`${API}/events/${ev.data.event_id}/challenges`, {
          params: { anon_id: getAnonId() },
        });
        if (cancelled) return;
        setChallenges(list.data || []);
        trackGrowth('challenge_view', { metadata: { event_id: ev.data.event_id, count: (list.data || []).length } });
      } catch (e) {
        if (!cancelled) setError('Could not load challenges. Please refresh.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const trending = [...challenges].sort((a, b) => b.total_entries - a.total_entries).slice(0, 5);
  const teacher = challenges.filter((c) => c.category === 'teacher');
  const total = challenges.length;
  const completed = challenges.filter((c) => c.total_entries > 0).length;

  const goLeaderboard = (chal) => {
    navigate(`/prom/${slug}/challenges/${chal.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center" data-testid="prom-challenges-loading">
        <Sparkle size={28} weight="fill" className="text-[#C29D52] animate-pulse" />
      </div>
    );
  }
  if (error) {
    return <div className="min-h-screen bg-neutral-950 text-white p-6 text-center" data-testid="prom-challenges-error">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-5 py-8" data-testid="prom-challenges-page">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-6"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B89762]/30/10 border border-[#B89762]/40/30 text-[#C29D52]/85 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
            <GraduationCap size={11} weight="fill" /> {event?.event_name || 'Prom'}
          </div>
          <h1 className="text-3xl font-black mb-1 flex items-center gap-2" data-testid="prom-challenges-title">
            <Trophy size={26} weight="fill" className="text-amber-300" /> Prom Challenges
          </h1>
          <p className="text-white/60 text-sm">
            Take part, vote for the best, climb the leaderboard.
          </p>
          <p className="text-[12px] text-white/45 mt-2" data-testid="prom-challenges-progress">
            <Sparkle size={10} weight="fill" className="inline mr-1 text-[#C29D52]" />
            {completed} of {total} challenges have entries
          </p>
          <div className="mt-3">
            <LiveActivityTicker eventId={event?.event_id} fallback="🔥 Already getting started" />
          </div>
        </motion.div>

        <Section title="Trending" icon={<Flame size={16} weight="fill" className="text-amber-300" />} testid="section-trending">
          {trending.length === 0 ? (
            <EmptyHint text="Be the first 🔥 — submit a photo to any challenge below" />
          ) : (
            trending.map((c) => <ChallengeCard key={c.id} c={c} onClick={() => goLeaderboard(c)} />)
          )}
        </Section>

        {teacher.length > 0 && (
          <Section title="Teacher moments" icon={<ChalkboardTeacher size={16} weight="fill" className="text-[#C29D52]" />} testid="section-teacher">
            {teacher.map((c) => <ChallengeCard key={c.id} c={c} onClick={() => goLeaderboard(c)} />)}
          </Section>
        )}

        <Section title="All challenges" icon={<Trophy size={16} weight="fill" className="text-white/70" />} testid="section-all">
          {challenges.map((c) => <ChallengeCard key={c.id} c={c} onClick={() => goLeaderboard(c)} />)}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon, testid, children }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-white/55 mb-3 flex items-center gap-1.5">
        {icon} {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function ChallengeCard({ c, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl bg-white/[0.04] border border-white/8 hover:bg-white/[0.07] transition-colors p-3 flex items-center gap-3"
      data-testid={`challenge-card-${c.id}`}
    >
      {/* Thumbnails */}
      <div className="shrink-0 flex -space-x-2">
        {[0, 1, 2].map((i) => {
          const url = c.preview_media[i];
          return url ? (
            <img
              key={i}
              src={url.startsWith('http') ? url : getMediaUrl(url, `${API}/photos/${url}`)}
              alt=""
              className="w-9 h-9 rounded-lg border border-neutral-800 object-cover bg-neutral-900"
            />
          ) : (
            <div key={i} className="w-9 h-9 rounded-lg border border-neutral-800 bg-white/[0.03]" />
          );
        })}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold truncate">{c.title}</p>
        <p className="text-[11px] text-white/45 mt-0.5">
          <span className="text-white/65 font-semibold">{c.total_entries}</span> entries · <span className="text-white/65 font-semibold">{c.total_votes}</span> votes
          {c.category === 'teacher' && <span className="ml-1.5 text-[#C29D52]/80">· {CATEGORY_LABEL[c.category]}</span>}
        </p>
      </div>
      <ArrowRight size={14} weight="bold" className="text-white/45 shrink-0" />
    </button>
  );
}

function EmptyHint({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-4 text-center text-[12px] text-white/55">
      {text}
    </div>
  );
}
